import React from "react";
import { ChevronDownIcon, ChevronUpIcon, ArrowPathIcon } from "@heroicons/react/24/outline";

export default function OrderHistoryTab({ loading, orders, expandedOrders, onToggleOrderExpand }) {
    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-48">
                <ArrowPathIcon className="h-7 w-7 text-gray-950 animate-spin" />
                <p className="text-xs text-gray-500 mt-2.5 font-bold uppercase tracking-wider">Fetching Order History...</p>
            </div>
        );
    }

    if (!Array.isArray(orders) || orders.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <p className="text-sm font-semibold uppercase tracking-wider">No Orders Found</p>
            </div>
        );
    }

    return (
        <div className="space-y-3 w-full text-xs">
            {orders.map((order, idx) => {
                const orderId = order.order_id || `order-${idx}`;
                const isExpanded = !!expandedOrders[orderId];
                const orderStatus = order.status || "completed";
                const dateCreated = order.date_created || "N/A";
                const totalAmount = order.total || 0;
                const currencySymbol = order.currency === "INR" ? "₹" : (order.currency || "₹");
                const paymentMethod = order.payment_method || order.payment_method_title || "N/A";

                const billing = order.billing || {};
                const billingName = `${billing.first_name || ""} ${billing.last_name || ""}`.trim() || "N/A";
                const email = billing.email || "N/A";
                const phone = billing.phone || "N/A";
                const credits = order.credits || 0;
                const items = order.items || order.line_items || [];

                return (
                    <div key={orderId} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-xs transition-all duration-200">
                        {/* Card Header */}
                        <div
                            onClick={() => onToggleOrderExpand(orderId)}
                            className="flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 cursor-pointer select-none border-b border-gray-150"
                        >
                            <div className="flex items-center gap-3">
                                <span className="font-medium text-gray-900">Order #{orderId}</span>
                                <span className="text-gray-300">|</span>
                                <span className="text-gray-500 font-medium">{dateCreated}</span>
                            </div>
                            <div className="flex items-center gap-4">
                                <span className="font-medium text-gray-955">{currencySymbol}{totalAmount}</span>
                                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${orderStatus === "completed" || orderStatus === "processing"
                                    ? "bg-green-50 text-green-700 border border-green-200"
                                    : "bg-amber-50 text-amber-700 border border-amber-200"
                                    }`}>
                                    {orderStatus}
                                </span>
                                {isExpanded ? (
                                    <ChevronUpIcon className="h-4 w-4 text-gray-700 stroke-[2.5]" />
                                ) : (
                                    <ChevronDownIcon className="h-4 w-4 text-gray-700 stroke-[2.5]" />
                                )}
                            </div>
                        </div>

                        {/* Card Body */}
                        {isExpanded && (
                            <div className="p-4 space-y-4 text-xs bg-white border-t border-gray-50">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-gray-100 pb-4">
                                    <div>
                                        <p className="text-gray-400 font-bold uppercase tracking-wider text-[9px]">Billing Name</p>
                                        <p className="font-semibold text-gray-800 mt-1">{billingName}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-400 font-bold uppercase tracking-wider text-[9px]">Contact</p>
                                        <p className="font-semibold text-gray-800 mt-1">{email} <br /> {phone}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-400 font-bold uppercase tracking-wider text-[9px]">Payment & Credits</p>
                                        <p className="font-semibold text-gray-800 mt-1">Method: {paymentMethod} <br /> Credits: {credits}</p>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="font-bold text-gray-900 mb-2.5 uppercase tracking-wider text-[10px]">Ordered Items ({items.length})</h4>
                                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                        <table className="min-w-full divide-y divide-gray-200 text-left text-[11px]">
                                            <thead className="bg-gray-50 text-gray-700 font-bold uppercase tracking-wider">
                                                <tr>
                                                    <th className="px-3 py-2.5">Product Name</th>
                                                    <th className="px-3 py-2.5 text-center">Qty</th>
                                                    <th className="px-3 py-2.5 text-right">Price</th>
                                                    <th className="px-3 py-2.5 text-center">Credits</th>
                                                    <th className="px-3 py-2.5 text-right">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-150 text-gray-600 bg-white">
                                                {items.map((item, idx) => (
                                                    <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                                                        <td className="px-3 py-2.5 font-medium text-gray-900">{item.name || "—"}</td>
                                                        <td className="px-3 py-2.5 text-center font-semibold text-gray-800">{item.quantity}</td>
                                                        <td className="px-3 py-2.5 text-right font-medium">{currencySymbol}{item.price}</td>
                                                        <td className="px-3 py-2.5 text-center text-blue-600 font-bold">{item.credits}</td>
                                                        <td className="px-3 py-2.5 text-right">
                                                            <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${item.issue_or_return === "issue" || item.status === "issue"
                                                                ? "bg-blue-50 text-blue-700"
                                                                : "bg-amber-50 text-amber-700"
                                                                }`}>
                                                                {item.issue_or_return || item.status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
